import { cn } from 'design-system'
import { X } from 'lucide-react'
import React from 'react'
import { connect } from 'react-redux'
import { compose, withHandlers } from 'recompose'

import withTimer from 'src/components/util/withTimer'

import * as constants from './constants'
import * as snackbarSlice from 'src/store/snackbar'

import ProgressBar from 'src/components/generics/progress-bar'
import withKeyHandlers from 'src/components/util/withKeyHandlers'

const timeToDismiss = 5000

class Snackbar extends React.Component {
  static defaultProps = {
    open: false,
    msg: null,
    assertive: false,
    type: constants.types.SUCCESS
  }

  componentDidMount() {
    const { open, initTimer } = this.props

    if (open) {
      initTimer()
    }
  }

  componentWillUnmount() {
    this.props.stopTimer()
  }

  componentDidUpdate(prevProps) {
    const { open, handleClose, initTimer, timerValue } = this.props
    const { open: prevOpen } = prevProps

    if (timerValue >= timeToDismiss) {
      handleClose()
    }

    if (!prevOpen && open) {
      initTimer()
    }
  }

  render() {
    const {
      open,
      assertive,
      msg,
      type,
      handleClose,
      timerValue,
      pauseTimer,
      resumeTimer
    } = this.props
    const messageType = type || constants.types.INFO

    return (
      <div
        className={cn(
          'fixed bottom-0 left-1/2 z-20 mb-4 w-auto max-w-full -translate-x-1/2 rounded-md border border-solid text-black',
          {
            'border-red-950 bg-red-50': messageType === 'error',
            'border-yellow-600 bg-amber-50': messageType === 'warning',
            'border-green-900 bg-green-50': messageType === 'success',
            'border-blue-900 bg-blue-50': messageType === 'info',
            hidden: !open
          }
        )}
        onMouseOver={pauseTimer}
        onMouseOut={resumeTimer}>
        <div className="px-8 py-4">
          <div className="sr-only">{messageType}</div>
          <p
            role="status"
            className="font-base whitespace-pre"
            aria-live={assertive ? 'assertive' : 'polite'}>
            {msg}
          </p>
        </div>
        <button
          className={cn(
            'absolute right-0 top-0 flex -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-green-600 p-1 text-white transition-colors hover:bg-green-300 hover:text-slate-950',
            {
              'bg-red-600 hover:bg-red-300': messageType === 'error',
              'bg-amber-600 hover:bg-amber-300': messageType === 'warning',
              'bg-green-600 hover:bg-green-300': messageType === 'success',
              'bg-blue-600 hover:bg-blue-300': messageType === 'info'
            }
          )}
          aria-label="Dismiss notification"
          onClick={handleClose}>
          <X size={16} aria-hidden />
        </button>
        <div className="absolute bottom-0 w-full overflow-hidden rounded-b-md">
          <ProgressBar
            percent={(timerValue * 100) / timeToDismiss}
            theme={messageType}
          />
        </div>
      </div>
    )
  }
}

const mapDispatchToProps = {
  close: snackbarSlice.actions.closeSnackbar
}

export { constants }

export default compose(
  connect(({ snackbar }) => ({ ...snackbar }), mapDispatchToProps),
  withTimer,
  withHandlers({
    handleClose:
      ({ close, stopTimer }) =>
      () => {
        close()
        stopTimer()
      }
  }),
  withKeyHandlers({
    keys: {
      ESC: ({ open, handleClose }) => {
        if (open) {
          handleClose()
        }
      },
      SPACE: ({ open, paused, pauseTimer }) => {
        if (open && !paused) {
          pauseTimer()
        }
      }
    }
  })
)(Snackbar)
